/**
 * Manual resolve is a workflow mark. It does not change hold, mismatch,
 * recharge, queue, or bill id. Those still decide push and receipts_blocked.
 */
import { formatMinor, sterlingGrossPence } from './currency.js';
import { formatReceiptDate, isoDateOnly } from './receiptDate.js';
import { receiptInXero, rechargeAnswered } from './receiptGroups.js';

export const MANUAL_RESOLVE_REASONS = [
  'handled_in_xero',
  'duplicate',
  'not_recoverable',
  'other',
] as const;

export type ManualResolveReason = (typeof MANUAL_RESOLVE_REASONS)[number];

export const MANUAL_RESOLVE_REASON_LABEL: Record<ManualResolveReason, string> = {
  handled_in_xero: 'Handled in Xero',
  duplicate: 'Duplicate',
  not_recoverable: 'Not recoverable',
  other: 'Other',
};

export function isManualResolveReason(
  value: string,
): value is ManualResolveReason {
  return (MANUAL_RESOLVE_REASONS as readonly string[]).includes(value);
}

/** "Resolved by me · Handled in Xero". Null when the rules placed the row. */
export function manualResolveLabel(
  reason: string | null | undefined,
): string | null {
  if (!reason || !isManualResolveReason(reason)) return null;
  return `Resolved by me · ${MANUAL_RESOLVE_REASON_LABEL[reason]}`;
}

export function parseManualResolve(raw: {
  reason?: unknown;
  note?: unknown;
}):
  | { ok: true; reason: ManualResolveReason; note: string | null }
  | { ok: false; message: string } {
  if (typeof raw.reason !== 'string' || !isManualResolveReason(raw.reason)) {
    return { ok: false, message: 'Pick a reason.' };
  }
  let note: string | null = null;
  if (raw.note != null && raw.note !== '') {
    if (typeof raw.note !== 'string') {
      return { ok: false, message: 'Note must be text.' };
    }
    const trimmed = raw.note.trim();
    if (trimmed.length > 500) {
      return { ok: false, message: 'Note is limited to 500 characters.' };
    }
    note = trimmed.length > 0 ? trimmed : null;
  }
  if (raw.reason === 'other' && !note) {
    return { ok: false, message: 'Other needs a note.' };
  }
  return { ok: true, reason: raw.reason, note };
}

export type ManualResolveSubject = {
  supplier_name?: string | null;
  currency?: string | null;
  gross_pence?: number | null;
  gbp_gross_pence?: number | null;
  status: string;
  xero_bill_id?: string | null;
  rechargeable?: boolean | null;
  recharge_job_id?: string | null;
  held?: boolean | null;
  held_reason?: string | null;
  xero_date_mismatch?: boolean | null;
  xero_total_mismatch?: boolean | null;
  xero_contact_map_mismatch?: boolean | null;
  receipt_date?: unknown;
  xero_bill_date?: unknown;
};

function shortDate(iso: string, other: string): string {
  const full = formatReceiptDate(iso);
  return iso.slice(0, 4) === other.slice(0, 4)
    ? full.replace(/ \d{4}$/, '')
    : full;
}

/**
 * Every line that applies, in order: unrecharged, held, then each mismatch.
 * Empty when nothing is being given up.
 */
export function manualResolveWarnings(receipt: ManualResolveSubject): string[] {
  const lines: string[] = [];
  if (
    receiptInXero({
      status: receipt.status,
      xero_bill_id: receipt.xero_bill_id,
    }) &&
    !rechargeAnswered({
      status: receipt.status,
      rechargeable: receipt.rechargeable,
      recharge_job_id: receipt.recharge_job_id,
    })
  ) {
    const name = receipt.supplier_name?.trim() || 'This receipt';
    const sterling = sterlingGrossPence({
      currency: receipt.currency ?? null,
      gross_pence: receipt.gross_pence ?? null,
      gbp_gross_pence: receipt.gbp_gross_pence ?? null,
    });
    const amount =
      sterling == null ? '' : ` ${formatMinor('GBP', sterling)}`;
    lines.push(
      `${name}${amount} has not been recharged to a client. Resolving means Terratech absorbs this cost.`,
    );
  }
  if (receipt.held) {
    const reason = receipt.held_reason?.trim();
    const lead = reason ? `This is held: ${reason}.` : 'This is held.';
    lines.push(
      `${lead} Resolving hides it from your list; the hold stays and it still will not post to Xero.`,
    );
  }
  if (receipt.xero_date_mismatch) {
    const receiptDate = isoDateOnly(receipt.receipt_date);
    const billDate = isoDateOnly(receipt.xero_bill_date);
    lines.push(
      receiptDate && billDate && receiptDate !== billDate
        ? `The Xero bill date is ${shortDate(billDate, receiptDate)} and the receipt is ${shortDate(receiptDate, billDate)}. Resolving leaves the bill wrong in Xero.`
        : 'The Xero bill date differs from this receipt. Resolving leaves the bill wrong in Xero.',
    );
  }
  if (receipt.xero_total_mismatch) {
    lines.push(
      'The Xero bill total does not match this receipt. Resolving leaves the bill wrong in Xero.',
    );
  }
  if (receipt.xero_contact_map_mismatch) {
    lines.push(
      'The Xero contact differs from the mapped contact. Resolving leaves the bill wrong in Xero.',
    );
  }
  return lines;
}
